import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, DataTable, Card, Input, Localized, Select } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";
import SEOFields from "../../components/admin/SEOFields";

const empty = () => ({
  slug: "",
  title: { tr: "", en: "", ar: "" },
  excerpt: { tr: "", en: "", ar: "" },
  body: { tr: "", en: "", ar: "" },
  quote: { tr: "", en: "", ar: "" },
  category: "news",
  cover_image: "",
  published_at: new Date().toISOString(),
  author: "",
  video_url: "",
  source_name: "",
  source_url: "",
  source_logo: "",
  tags: [],
  read_minutes: 0,
  featured: false,
  seo: {},
});

const CATS = [
  { value: "news", label: "Haber" },
  { value: "video", label: "Video" },
  { value: "blog", label: "Blog" },
  { value: "press", label: "Basında Biz" },
  { value: "event", label: "Etkinlik / Zirve" },
  { value: "publication", label: "Yayın" },
];

const AdminNewsPage = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/news").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editing?.new) await api.post("/admin/news", form);
      else await api.put(`/admin/news/${editing.id}`, form);
      toast.success("Kaydedildi"); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`"${r.slug}" silinsin mi?`)) return;
    await api.delete(`/admin/news/${r.id}`); load();
  };

  if (editing) return (
    <div data-testid="admin-news-form">
      <AdminHeader eyebrow="Media Center" title={editing.new ? "Yeni Haber" : "Haberi Düzenle"}
        action={<div className="flex gap-3"><Btn variant="ghost" onClick={() => setEditing(null)}>İptal</Btn><Btn onClick={save} disabled={busy} testId="news-save">Kaydet</Btn></div>} />
      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="İçerik">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="news-slug" />
              <Select label="Tür / Kategori" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATS} testId="news-cat" />
              <Input label="Yazar" value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
              <Input label="Yayın Tarihi (ISO)" value={form.published_at} onChange={(v) => setForm({ ...form, published_at: v })} />
              <Input label="Okuma Süresi (dk, blog için)" type="number" value={form.read_minutes || 0} onChange={(v) => setForm({ ...form, read_minutes: Number(v) || 0 })} />
              <Input label="Etiketler (virgülle ayır)" value={(form.tags || []).join(", ")} onChange={(v) => setForm({ ...form, tags: v.split(",").map((t) => t.trim()).filter(Boolean) })} />
            </div>
            <div className="mt-6"><Localized label="Başlık" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="news-title" /></div>
            <div className="mt-6"><Localized label="Özet" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} textarea /></div>
            <div className="mt-6"><Localized label="İçerik" value={form.body} onChange={(v) => setForm({ ...form, body: v })} textarea /></div>
          </Card>

          <Card title="Video (Video türü için)">
            <p className="text-xs text-brand-medium mb-3">
              YouTube URL veya doğrudan .mp4 linki yapıştırın. Video URL doluysa medya kartında play butonu görünür ve tıklandığında modal olarak açılır.
            </p>
            <Input label="Video URL" value={form.video_url || ""} onChange={(v) => setForm({ ...form, video_url: v })} testId="news-video-url" />
          </Card>

          <Card title="Kaynak / Basında Biz (Alıntı türü için)">
            <p className="text-xs text-brand-medium mb-3">
              Haberi hangi gazete/site yayınladıysa buraya yazın. Kaynak yazısı ile kaynak logosu tanıtım kartlarında görünür. Alıntı alanına yayınlanan yazının çarpıcı bir cümlesini girin.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Kaynak Adı" value={form.source_name || ""} onChange={(v) => setForm({ ...form, source_name: v })} testId="news-source-name" />
              <Input label="Kaynak URL (dış link)" value={form.source_url || ""} onChange={(v) => setForm({ ...form, source_url: v })} testId="news-source-url" />
            </div>
            <div className="mt-4">
              <ImageUpload label="Kaynak Logosu (opsiyonel)" value={form.source_logo || ""} onChange={(v) => setForm({ ...form, source_logo: v })} testId="news-source-logo" />
            </div>
            <div className="mt-4"><Localized label="Alıntı Cümlesi" value={form.quote} onChange={(v) => setForm({ ...form, quote: v })} textarea /></div>
          </Card>
        </div>
        <div className="space-y-6">
          <Card title="Görsel"><ImageUpload label="Kapak Görseli" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testId="news-cover-upload" /></Card>
          <Card title="Yayın">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              <span className="text-sm">Öne çıkarılsın</span>
            </label>
          </Card>
          <SEOFields
            value={form.seo}
            onChange={(seo) => setForm({ ...form, seo })}
            context={{
              title: form.title?.tr || form.title?.en || form.slug,
              content: [form.excerpt?.tr, form.body?.tr, form.excerpt?.en, form.body?.en].filter(Boolean).join(" \n").slice(0, 3000),
              category: form.category || "news",
              slug: `media/${form.slug}`,
            }}
          />
        </div>
      </form>
    </div>
  );

  return (
    <div data-testid="admin-news-page">
      <AdminHeader eyebrow="Media Center" title="Haberler" action={<Btn onClick={() => { setEditing({ new: true }); setForm(empty()); }} testId="new-news"><Plus size={14} /> Yeni</Btn>} />
      <DataTable
        columns={[
          { key: "title", label: "Başlık", render: (r) => r.title?.tr || r.slug },
          { key: "category", label: "Kategori" },
          { key: "published_at", label: "Yayın", render: (r) => (r.published_at || "").substring(0, 10) },
          { key: "actions", label: "", render: (r) => (
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => { setEditing(r); setForm({ ...empty(), ...r }); }}>Düzenle</Btn>
              <Btn variant="danger" onClick={() => remove(r)}><Trash2 size={12} /></Btn>
            </div>
          ) },
        ]}
        rows={rows}
      />
    </div>
  );
};

export default AdminNewsPage;
